import React from "react";
import { useTranslation } from "react-i18next";
import { PeriodFxRates } from "../PeriodFxRates";
import { MonthlyFxRates } from "../MonthlyFxRates";
import { InfoHint } from "../InfoHint";
import { CircleNotch, Info, DownloadSimple, CheckCircle } from "@phosphor-icons/react";
import { splitPairsByTarget } from "./flaggedFx";
import { monthsBetween, monthlyFxPairs, periodFxSources } from "./fxNeeds";
import { authClient } from "../../lib/authApi";

// A2-467 (operator 2026-10-20: "I can't see currency exchange rates anymore.
// Has this been silently deleted?") — the section never disappears: when no
// rate is needed it says so, in words, instead of showing a bare grey title.
export const FxNote = ({ testid, icon = null, warn = false, children }) => (
  <p data-testid={testid} className={`flex items-start gap-2 px-4 py-3 text-sm ${warn
    ? "text-amber-800 dark:text-amber-200 bg-amber-500/10 dark:bg-amber-900/30" : "text-slate-700 dark:text-slate-200"}`}>
    {icon}<span>{children}</span>
  </p>
);

// v0.6 P2 cap sweep — extracted verbatim from CalculatePanel.jsx.
// Exchange-rate entry surface: the OSS/UK aggregate (single end-of-period
// ECB rate) and the per-country monthly national grid are two legally
// distinct conversion rules, rendered together for the operator.
export const FxRatesSection = ({
  selectedRuns,
  flaggedFxPairs,
  flaggedMonthlyFxPairs,
  regime,
  periodFxRates,
  setPeriodFxRates,
  monthlyFxRates,
  setMonthlyFxRates,
  registrations,
  periodStart,
  periodEnd,
  onApply,
  applyBusy,
  previewLoading = false,
  mayBeIncomplete = false,
  previewAnswered = false,
}) => {
  const { t } = useTranslation();
  const showFx = selectedRuns.length > 0 || flaggedFxPairs.length > 0;
  // v0.6 (period_fx_rates two-target fix) — a UK_VAT calc can need BOTH
  // a GBP box (GB-attributed MARKETPLACE/EXPORT/domestic-REVERSE_CHARGE
  // rows) and a EUR box (genuine cross-border OSS rows, always EUR by
  // law) at once — see engine.calc_row_rules.apply_row_fx_flag's
  // docstring. periodFxRates is now {TARGET: {SOURCE: rate}}; render
  // PeriodFxRates once per target actually needed instead of assuming
  // a single calc-wide base. An EU_OSS calc never needs a second box —
  // its regime base already IS EUR, matching every non-national row's
  // real target (see _row_filing_base's fallback branch).
  const regimeBase = regime === "UK_VAT" ? "GBP" : "EUR";
  const { gbp: gbpExtra, eur: eurExtra } = splitPairsByTarget(flaggedFxPairs);
  const regimeExtra = regimeBase === "GBP" ? gbpExtra : eurExtra;
  const showEurBox = regimeBase !== "EUR" && eurExtra.length > 0;
  const setRatesFor = (target) => (next) =>
    setPeriodFxRates({ ...periodFxRates, [target]: next });
  // The same rules the rate boxes below use (./fxNeeds), so this can never
  // say "nothing needed" while a box is asking for a rate.
  const monthlyNeeded = selectedRuns.length > 0 && monthsBetween(periodStart, periodEnd).length > 0
    && monthlyFxPairs(selectedRuns, registrations, flaggedMonthlyFxPairs).length > 0;
  const nothingToConvert = showFx && previewAnswered && !previewLoading && !mayBeIncomplete && !showEurBox && !monthlyNeeded
    && periodFxSources(selectedRuns, regimeBase, regimeExtra).length === 0;

  // v0.6.9 — Settings > Exchange Rates preset. Fills any BLANK cell
  // below from the operator's saved rate table; never overwrites a
  // value already typed in this session — a hand-edit for one specific
  // calc still takes precedence, matching the per-calc override this
  // section has always supported.
  const [loadingPreset, setLoadingPreset] = React.useState(false);
  const [presetMsg, setPresetMsg] = React.useState(null);
  const loadPresetRates = async () => {
    setLoadingPreset(true);
    setPresetMsg(null);
    try {
      const { data } = await authClient.get("/settings/fx-rates");
      const entries = data?.rates || [];
      let filled = 0;

      // v0.6.10 — a preset row now carries a rate_type ("period" |
      // "monthly"), set at upload time via the "Rate Type" column
      // (default "Both", which the backend expands into ONE row per
      // type — see routes_settings_fx_rates.py's module docstring for
      // why: the period-end and monthly rates are legally distinct
      // and can genuinely differ, so one number silently satisfying
      // both boxes was itself the bug. A legacy row with no rate_type
      // at all (uploaded before this existed) still matches both,
      // for backward compatibility.
      const nextMonthly = { ...(monthlyFxRates || {}) };
      for (const e of entries) {
        if (e.rate_type && e.rate_type !== "monthly") continue;
        const existing = nextMonthly?.[e.source]?.[e.target]?.[e.month];
        if (existing && String(existing.rate || "").trim()) continue;
        nextMonthly[e.source] = { ...(nextMonthly[e.source] || {}) };
        nextMonthly[e.source][e.target] = {
          ...(nextMonthly[e.source][e.target] || {}),
        };
        nextMonthly[e.source][e.target][e.month] = {
          rate: e.rate, source: e.note || "",
        };
        filled += 1;
      }
      setMonthlyFxRates(nextMonthly);

      // The OSS/UK aggregate box needs ONE rate per pair (the ECB
      // end-of-period rate), not a monthly grid — use the preset row
      // for whichever month the calc's own period actually ends in.
      // v0.6.10 — the preset table is intentionally general-purpose
      // (any source/target pair, since it ALSO feeds the monthly
      // national-return grid above, which genuinely needs non-GBP/EUR
      // targets like PLN or SGD). This period box is legally GBP/EUR
      // only (backend/engine/calc_request.py's normalize_period_fx_
      // rates rejects anything else) — this loop used to copy ANY
      // preset entry matching the month, including ones with a target
      // meant only for the monthly grid, straight into this box.
      // Reported live: "period_fx_rates target must be GBP or EUR;
      // got 'SGD'" on Calculate, from a preset entry that had nothing
      // to do with the OSS/UK box at all.
      const endMonth = (periodEnd || "").slice(0, 7);
      const nextPeriod = { ...(periodFxRates || {}) };
      for (const e of entries) {
        if (e.rate_type && e.rate_type !== "period") continue;
        if (e.month !== endMonth) continue;
        if (e.target !== "GBP" && e.target !== "EUR") continue;
        const existing = nextPeriod?.[e.target]?.[e.source];
        if (existing && String(existing).trim()) continue;
        nextPeriod[e.target] = {
          ...(nextPeriod[e.target] || {}), [e.source]: e.rate,
        };
        filled += 1;
      }
      setPeriodFxRates(nextPeriod);

      setPresetMsg(
        filled > 0
          ? `Loaded ${filled} rate(s) from your saved preset.`
          : "No new rates to load — every cell already has a value, or nothing in your preset matches this period.",
      );
    } catch {
      setPresetMsg("Could not load saved exchange rates — check Settings > Exchange Rates.");
    } finally {
      setLoadingPreset(false);
    }
  };

  if (!showFx) {
    return <FxNote testid="fx-section-waiting">{t("ui.calculate.fx_section.waiting", "Tick at least one file in part B. Any exchange rates the calculation needs will be asked for here.")}</FxNote>;
  }
  if (nothingToConvert) {
    return (
      <FxNote testid="fx-section-none-needed" icon={<CheckCircle size={18} weight="bold" className="shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />}>
        {t("ui.calculate.fx_section.none_needed", "No exchange rates needed: every amount in the ticked files is already in the currency its return is filed in.")}
      </FxNote>
    );
  }
  return (
    <>
      {showFx && (
        <div
          data-testid="fx-section-intro"
          className="border-t border-slate-200 dark:border-slate-700 px-4 pt-3 pb-2 bg-white dark:bg-slate-900"
        >
          <div className="flex items-center gap-1">
            <span className="text-[10px] font-bold tracking-wide uppercase text-slate-500 dark:text-slate-400 tabular-nums">
              {t("ui.calculate.fx_section.title", { defaultValue: "Exchange rates" })}
            </span>
            <InfoHint
              testid="fx-section-intro-info"
              label={t("ui.calculate.fx_section.intro_info", { defaultValue: "These are two legally distinct conversion rules, not duplicate entry. OSS / UK aggregate returns use a SINGLE end-of-period ECB reference rate; per-country national returns use each country's MONTHLY published rate (BMF/ECB/HMRC). Do not copy one into the other. Rates are saved when you press Calculate below — they are stored with the calculation and restored when you reopen it." })}
            />
            <button
              type="button"
              data-testid="fx-load-preset-button"
              onClick={loadPresetRates}
              disabled={loadingPreset}
              title={t("ui.calculate.fx_section.load_preset_title", { defaultValue: "Fill blank rate boxes below from Settings > Exchange Rates" })}
              className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-teal-700 dark:text-teal-300 bg-teal-600/10 hover:bg-teal-600/20 border border-teal-400/50 px-1.5 py-0.5 rounded disabled:opacity-50"
            >
              {loadingPreset
                ? <CircleNotch size={10} weight="bold" className="animate-spin" />
                : <DownloadSimple size={10} weight="bold" />}
              {t("ui.calculate.fx_section.load_preset", { defaultValue: "Load preset rates" })}
            </button>
            {/* v0.6 Stage 2 — the currency list below now comes from
                actually classifying the data (POST /calculate/fx-preview),
                not just the currencies declared on the upload. This spinner
                is the only visible sign that lookup is in flight, since the
                boxes below can still shift after it lands. */}
            {previewLoading && (
              <span
                data-testid="fx-preview-loading"
                className="ml-1 inline-flex items-center gap-1 text-[10px] text-slate-400 dark:text-slate-500 tabular-nums"
                title={t("ui.calculate.fx_section.checking_title", { defaultValue: "Checking your data for every exchange rate it will need…" })}
              >
                <CircleNotch size={11} weight="bold" className="animate-spin" />
                {t("ui.calculate.fx_section.checking", { defaultValue: "checking…" })}
              </span>
            )}
          </div>
          {presetMsg && (
            <p
              data-testid="fx-preset-message"
              className="mt-1 text-[10px] text-slate-500 dark:text-slate-400"
            >
              {presetMsg}
            </p>
          )}
          {/* v0.6 — promoted out of the click-to-reveal InfoHint above:
              an operator filling in the per-country grid below kept
              assuming it also satisfied the OSS/UK box, since both
              can show "EUR → GBP" with a different number attached.
              This one line is the fact that actually prevents that
              mistake, so it can't be missed behind a tap on a 14px
              icon. */}
          <p
            data-testid="fx-section-two-rates-notice"
            className="mt-1 text-[10px] text-amber-700 dark:text-amber-300 leading-snug"
          >
            {t("ui.calculate.fx_section.two_rates", { defaultValue: "Two separate rates, even when the currency pair looks the same: the box below is one ECB rate for the whole period; the table further down is each country's own monthly rate. Filling in one does not fill in the other." })}
          </p>
          {/* v0.6 Stage 2 — may_be_incomplete from the preview: a narrow,
              known-limitation caveat (see memory/FX_PREVIEW_STAGE1_SPEC.md),
              not a general disclaimer — only shown when the preview itself
              flagged this batch as one of the cases it can't fully resolve
              upfront. The post-calc missing-rate flow (below, unchanged)
              stays the safety net for exactly this case. */}
          {mayBeIncomplete && (
            <p
              data-testid="fx-preview-incomplete-notice"
              className="mt-1 flex items-start gap-1 text-[10px] text-slate-500 dark:text-slate-400 leading-snug"
            >
              <Info size={12} weight="bold" className="mt-px shrink-0" />
              {t("ui.calculate.fx_section.incomplete", { defaultValue: "This list is best-effort, not guaranteed-complete — this batch has cross-border EU sales near a threshold that can only be confirmed once real rates are entered. If a rate is still missing after you run the calculation, you'll be prompted for it then." })}
            </p>
          )}
        </div>
      )}

      {showFx && (
        <PeriodFxRates
          selectedRuns={selectedRuns}
          targetCurrency={regimeBase}
          rates={periodFxRates[regimeBase] || {}}
          setRates={setRatesFor(regimeBase)}
          extraCurrencies={regimeExtra}
          onApply={onApply}
          applyBusy={applyBusy}
          title={t("ui.calculate.fx_section.aggregate_title", { defaultValue: "OSS / UK aggregate return" })}
          hint={t("ui.calculate.fx_section.aggregate_hint", {
            defaultValue: "Enter the ECB reference rate for the last day of the tax period (the Directive rule for OSS AND IOSS returns — Art 369h for Union OSS, Art 366 for Non-Union, Art 369u for IOSS). If the ECB published no rate that day, use the next publication date. Example source: ECB rate 31/12/2025. This feeds the OSS / UK aggregate return, the IOSS XML export, and the XLSX export — it is NOT the monthly national-return rate below.",
          })}
          shortNote={t("ui.calculate.fx_section.aggregate_note", { defaultValue: "Separate from the per-country table below, even for the same currency pair — one ECB rate for the whole period." })}
        />
      )}

      {showEurBox && (
        <PeriodFxRates
          // Deliberately NOT selectedRuns — the declared_currency
          // heuristic assumed one calc-wide base and would over-include
          // currencies that never actually need a EUR conversion (e.g.
          // a run whose data is entirely GB-attributed MARKETPLACE).
          // The classification-derived eurExtra is the only reliable
          // signal that a source currency needs EUR specifically.
          selectedRuns={[]}
          targetCurrency="EUR"
          rates={periodFxRates.EUR || {}}
          setRates={setRatesFor("EUR")}
          extraCurrencies={eurExtra}
          onApply={onApply}
          applyBusy={applyBusy}
          title={t("ui.calculate.fx_section.eur_title", { defaultValue: "Cross-border OSS obligation (EUR)" })}
          hint={t("ui.calculate.fx_section.eur_hint", {
            defaultValue: "This UK_VAT calc also has cross-border EU sales that must file in EUR by law, separate from your GBP aggregate rate above. Enter the ECB reference rate for the last day of the tax period.",
          })}
          shortNote={t("ui.calculate.fx_section.eur_note", { defaultValue: "Separate from the GBP box above and the per-country table below — this is the EUR rate your OSS obligation needs." })}
        />
      )}

      {selectedRuns.length > 0 && (
        <MonthlyFxRates
          selectedRuns={selectedRuns}
          registrations={registrations}
          extraPairs={flaggedMonthlyFxPairs}
          periodStart={periodStart}
          periodEnd={periodEnd}
          value={monthlyFxRates}
          setValue={setMonthlyFxRates}
        />
      )}
    </>
  );
};
